import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostListener,
  OnInit,
  OnDestroy,
  inject,
  signal,
} from '@angular/core';
import { RouterOutlet, RouterLink, Router, NavigationEnd } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { AuthService } from '@auth0/auth0-angular';
import { ProfileMenuComponent } from '../profile-menu/profile-menu.component';
import { RouterUrlsEnum } from '../../enums/router-urls.enum';
import { MobileTabBarComponent, MobileTabI } from './mobile-tab-bar/mobile-tab-bar.component';
import { DarkModeToggleComponent } from '../../theme/dark-mode-toggle.component';
import { filter, map } from 'rxjs/operators';
import { Subscription } from 'rxjs';

interface NavItemI {
  path: string;
  label: string;
  icon: string;
  matchSegments: string[];
}

/**
 * A labelled cluster of destinations. Used by the mobile off-canvas drawer (the
 * desktop horizontal nav renders `primaryNav` / `operationsNav` / `settingsNav`
 * directly). The top (primary) group has a `null` header so it renders without a
 * section title; secondary groups carry a short header for scannability.
 */
interface NavGroupI {
  header: string | null;
  items: NavItemI[];
}

interface PageInfo {
  title: string;
  subtitle: string;
}

const TITLE_MAP: Record<string, PageInfo> = {
  'admin-dashboard': { title: 'Operations overview', subtitle: 'Last 7 days' },
  'manage-events': { title: 'Testing events', subtitle: 'Calendar · North Texas' },
  'manage-customers': { title: 'Members', subtitle: 'All regions' },
  'manage-bookings': { title: 'Bookings', subtitle: 'All regions' },
  'manage-clubs': { title: 'Partner gyms', subtitle: 'Active locations' },
  'manage-bodymeasurements': { title: 'Reports', subtitle: 'Body measurements' },
  'manage-payments': { title: 'Payments', subtitle: 'Revenue & utilization' },
  'manage-orders': { title: 'Orders', subtitle: 'All orders' },
  'manage-settings': { title: 'Settings', subtitle: 'Organization' },
  'manage-signups': { title: 'Sign-ups', subtitle: 'Registration pipeline' },
  'manage-discounts': { title: 'Discounts', subtitle: 'Promo codes' },
  'manage-regions': { title: 'Regions', subtitle: 'Coverage areas' },
  'manage-tenants': { title: 'Tenants', subtitle: 'Organizations' },
  // The live tenant route is `/tenants/...` (manageTenantUiRoutes, path: 'tenants'),
  // not `/auth/manage-tenants`. Map the real segment so the topbar title resolves.
  tenants: { title: 'Tenants', subtitle: 'Organizations' },
  'manage-operators': { title: 'Operators', subtitle: 'Truck staff' },
  'manage-users': { title: 'Users', subtitle: 'All users' },
  'admin-setup': { title: 'Company setup', subtitle: 'Configuration' },
  'admin-register': { title: 'Registration', subtitle: 'Operator onboarding' },
  license: { title: 'License', subtitle: 'Subscription management' },
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-admin-shell',
  templateUrl: './admin-shell.component.html',
  styleUrls: ['./admin-shell.component.scss'],
  imports: [RouterOutlet, RouterLink, AsyncPipe, ProfileMenuComponent, MobileTabBarComponent, DarkModeToggleComponent],
})
export class AdminShellComponent implements OnInit, OnDestroy {
  /** #2246 — slim internal footer year (marketing FooterComponent removed from the shell). */
  protected readonly currentYear = new Date().getFullYear();

  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  protected auth = inject(AuthService);

  /**
   * Admin's real account-settings route (profileUiRoutes, registered under
   * `auth/profile/details` since #2136 removed the dead `account` child — see
   * libs/front/shared/profile-ui/src/index.ts).
   * ProfileMenuComponent's default (AUTH_ACCOUNT, '/auth/account') is the
   * customer app's route and 404s here, falling through to the admin app's
   * `**` wildcard redirect to `home`.
   */
  protected readonly profileAccountUrl = RouterUrlsEnum.AUTH_PROFILE_ACCOUNT;

  /**
   * Horizontal top-nav model (replaces the old left sidebar). The primary
   * destinations render as flat links; the `Operations` cluster collapses into a
   * dropdown; and the former `Configuration` cluster (Regions, License, Tenants)
   * now lives inside the Settings hub (`/auth/manage-settings`) — so `Settings`
   * is the single right-aligned entry that also highlights for those routes.
   */
  readonly primaryNav: NavItemI[] = [
    { path: '/auth/admin-dashboard', label: 'Overview', icon: 'bx-grid-alt', matchSegments: ['admin-dashboard'] },
    { path: '/auth/manage-events', label: 'Events', icon: 'bx-calendar', matchSegments: ['manage-events'] },
    { path: '/auth/manage-customers', label: 'Members', icon: 'bx-group', matchSegments: ['manage-customers'] },
    { path: '/auth/manage-clubs', label: 'Partner gyms', icon: 'bx-buildings', matchSegments: ['manage-clubs'] },
    {
      path: '/auth/manage-bodymeasurements',
      label: 'Reports',
      icon: 'bx-line-chart',
      matchSegments: ['manage-bodymeasurements', 'manage-payments'],
    },
  ];

  readonly operationsNav: NavItemI[] = [
    { path: '/auth/manage-bookings', label: 'Bookings', icon: 'bx-book-open', matchSegments: ['manage-bookings'] },
    { path: '/auth/manage-orders', label: 'Orders', icon: 'bx-receipt', matchSegments: ['manage-orders'] },
    {
      path: '/auth/manage-discounts',
      label: 'Discounts',
      icon: 'bx-purchase-tag',
      matchSegments: ['manage-discounts'],
    },
    // #1868: carve-out from #1773 sub-item 7 — operators previously had no
    // list/edit UI at all (only a create-only Settings wizard step).
    { path: '/auth/manage-operators', label: 'Operators', icon: 'bx-id-card', matchSegments: ['manage-operators'] },
  ];

  /**
   * Settings — also the home for the former Configuration cluster, so it
   * highlights when viewing Regions / License / Tenants (reached via the hub).
   */
  readonly settingsNav: NavItemI = {
    path: '/auth/manage-settings',
    label: 'Settings',
    icon: 'bx-cog',
    matchSegments: ['manage-settings', 'admin-setup', 'manage-regions', 'license', 'tenants'],
  };

  /** Grouped view — drives the mobile off-canvas drawer. */
  readonly navGroups: NavGroupI[] = [
    { header: null, items: this.primaryNav },
    { header: 'Operations', items: this.operationsNav },
    { header: 'Configuration', items: [this.settingsNav] },
  ];

  /** Flat view of every destination — drives the mobile-tab derivation. */
  readonly navItems: NavItemI[] = [...this.primaryNav, ...this.operationsNav, this.settingsNav];

  /**
   * The four top-level destinations surfaced in the mobile bottom tab bar (<=767px).
   * Derived from `navItems` (filtered by path) so the tab bar stays in sync with the
   * desktop nav. The remaining destinations stay off the bar to keep it focused and
   * remain reachable from the off-canvas drawer.
   */
  private static readonly MOBILE_TAB_PATHS: readonly string[] = [
    '/auth/admin-dashboard',
    '/auth/manage-events',
    '/auth/manage-customers',
    '/auth/manage-bodymeasurements',
  ];

  readonly mobileTabs: MobileTabI[] = AdminShellComponent.MOBILE_TAB_PATHS.map(path => {
    const item = this.navItems.find(nav => nav.path === path);
    return {
      path,
      label: item?.label ?? '',
      icon: item?.icon ?? '',
    };
  });

  pageTitle = 'Admin';
  pageSubtitle = '';

  /**
   * True on routes whose leaf component already renders its own
   * `.adm-page__header` (back button + H1) — showing the shell's data-driven
   * band too stacks two headers, wasting ~250px of the phone viewport (P2
   * dark/mobile QA). Scoped to the single confirmed offender (the create-event
   * wizard) rather than every admin detail/list page that follows the same
   * `.adm-page__header` pattern — broadening this would need each of those
   * routes re-verified, out of scope for this fix.
   *
   * A signal (not a plain field, matching `operationsOpen`/`sidebarOpen`
   * below) so the OnPush view reliably re-renders when it flips on
   * navigation, without depending on the router-subscription's manual
   * `markForCheck()` call to also happen to catch this binding.
   */
  readonly hideHeaderBand = signal(false);

  private static readonly HIDDEN_HEADER_BAND_ROUTES: readonly string[] = ['/manage-events/create-event'];

  /** Desktop "Operations" dropdown state. */
  readonly operationsOpen = signal(false);

  /** Mobile off-canvas drawer state. Desktop ignores this (horizontal nav is always visible). */
  readonly sidebarOpen = signal(false);

  toggleOperations(event: Event): void {
    // Stop the bubble so the document:click handler below doesn't immediately re-close.
    event.stopPropagation();
    this.operationsOpen.update(open => !open);
  }

  toggleSidebar(): void {
    this.sidebarOpen.update(open => !open);
  }

  closeSidebar(): void {
    this.sidebarOpen.set(false);
  }

  /** Any click outside the open dropdown dismisses it. */
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.operationsOpen()) {
      this.operationsOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.operationsOpen.set(false);
    this.sidebarOpen.set(false);
  }

  private routeSub: Subscription | null = null;

  ngOnInit(): void {
    this.updatePageInfo(this.router.url);
    this.routeSub = this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        map(e => (e as NavigationEnd).urlAfterRedirects),
      )
      .subscribe(url => {
        this.updatePageInfo(url);
        // Close any open nav surfaces after navigating so the new page is visible.
        this.operationsOpen.set(false);
        this.sidebarOpen.set(false);
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  isNavItemActive(item: NavItemI): boolean {
    return item.matchSegments.some(seg => this.router.url.includes(seg));
  }

  /** True when the current route belongs to the Operations cluster (highlights the dropdown). */
  isOperationsActive(): boolean {
    return this.operationsNav.some(item => this.isNavItemActive(item));
  }

  /** Current router URL — fed to the mobile tab bar so the active tab is deterministic. */
  get currentUrl(): string {
    return this.router.url;
  }

  private updatePageInfo(url: string): void {
    this.hideHeaderBand.set(AdminShellComponent.HIDDEN_HEADER_BAND_ROUTES.some(route => url.includes(route)));

    const segments = url.split('/').filter(Boolean);
    for (const seg of segments) {
      if (TITLE_MAP[seg]) {
        this.pageTitle = TITLE_MAP[seg].title;
        this.pageSubtitle = TITLE_MAP[seg].subtitle;
        return;
      }
    }
    this.pageTitle = 'Admin';
    this.pageSubtitle = 'Admin console';
  }
}
